/**
 * Pure reading of the server path-mode state for the UI (Task 32 review): which switch is running, which base is
 * active, and whether the last finished switch left failures that still apply to the current mode.
 * Import-free of services so components and tests can use it without the API/Firebase graph.
 */
import type { ArticleModeSwitchJob, ArticlePathModeState } from '@modules/custom-domains/types/customDomain.types';

export interface ArticlePathModeView {
  /** The project's switch job (running or last finished). */
  job: ArticleModeSwitchJob | null;
  running: boolean;
  /** The stored base while path mode is on and no switch runs; null otherwise. */
  activeBaseUrl: string | null;
  /** Kind of the last finished switch whose failures still apply to the current mode; null when nothing failed. */
  failedKind: 'set' | 'clear' | null;
  /** Articles that switch did not republish (they still carry the old address). */
  failedCount: number;
  /** That switch's related-cards/index refresh failed. */
  relatedFailed: boolean;
}

export function pathModeViewOf(state: ArticlePathModeState | null): ArticlePathModeView {
  const job = state?.job ?? null;
  const running = job?.status === 'running';
  const activeBaseUrl = !running && state?.mode === 'path' && state.publicBaseUrl ? state.publicBaseUrl : null;
  const finished = running ? null : job;
  // A set's failures matter only for the base it switched to; a clear's only while the project stays in subdomain mode.
  const applies = Boolean(finished && (
    finished.kind === 'set'
      ? activeBaseUrl !== null && finished.publicBaseUrl === activeBaseUrl
      : activeBaseUrl === null && state?.mode !== 'path'
  ));
  const failedCount = applies && finished ? finished.results.filter((result) => result.status === 'failed').length : 0;
  const relatedFailed = Boolean(applies && finished?.relatedRefresh && !finished.relatedRefresh.ok);
  // A switch that failed before republishing (site files not rewritten) has no failed rows but still needs a re-run.
  const switchFailed = Boolean(applies && finished?.status === 'failed');
  const failedKind = finished && (failedCount > 0 || relatedFailed || switchFailed) ? finished.kind : null;
  return { job, running, activeBaseUrl, failedKind, failedCount, relatedFailed };
}
